import React, { useContext, useState } from "react";
import axios from "axios";

import { UserContext } from "../../../contexts";
import { Button, Chip, Icon, IconButton, Modal, Money, Segmented, Select } from "../../../components/ui";
import { useTradeMatch } from "../../../product/components/ProductPrices/useTradeImage";
import { AddProductPriceForm, PriceEvolution } from "../../../product/components";
import PriceChart from "../../../product/components/ProductPrices/PriceChart";
import {
  DEFAULT_PRICE_CHART_RANGE,
  PRICE_CHART_RANGE_OPTIONS,
} from "../../../product/components/ProductPrices/priceChartUtils";
import "../../../product/components/ProductPrices/price-chart.scss";
import {
  PRODUCT_PRICE_LIST_KEY,
  PRODUCT_PRICE_LIST_TABS,
  usePriceAnalytics,
  usePricesByDate,
  useProductPriceList,
  useRefreshPricingData,
} from "../../../product/hooks";
import {
  boxLabels,
  boxOptions,
  conditionLabels,
  conditionOptions,
  locationOptions,
  monthLabels,
  priceSortByOptions,
  sourceOptions,
  yearOptions,
} from "../../../product/constants";
import { MarketplaceActions } from "../../atoms";
import { useQueryClient } from "@tanstack/react-query";

const TABS = [
  { value: PRODUCT_PRICE_LIST_TABS.historical, label: "History" },
  { value: PRODUCT_PRICE_LIST_TABS.available, label: "Available" },
  { value: PRODUCT_PRICE_LIST_TABS.dealFinder, label: "Deal finder" },
];

// The same per-tab defaults PriceList applies (Available = Chrono24, cheapest first).
const TAB_DEFAULTS = {
  [PRODUCT_PRICE_LIST_TABS.historical]: { priceSortBy: "sort-by-date" },
  [PRODUCT_PRICE_LIST_TABS.available]: { source: "chrono24", priceSortBy: "sort-by-price" },
  [PRODUCT_PRICE_LIST_TABS.dealFinder]: { priceSortBy: "sort-by-date" },
};

const toOptions = (list) => list.map((o) => ({ value: String(o.id), label: String(o.name) }));
const SORT_OPTIONS = priceSortByOptions.map((o) => ({
  value: o.id,
  label: o.name.replace(/^SORT BY: /, "").replace(/^FILTER: /, "Only: "),
}));

const sourceName = (id) => sourceOptions.find((o) => o.id === id)?.name || id || "—";
const conditions = (raw) => {
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.map((c) => conditionLabels[c] || c).join(", ") : "";
  } catch (e) {
    return "";
  }
};
const yearMonth = (ym) => {
  if (!ym) return "";
  const month = monthLabels[Number(ym.substr(5, 2))];
  return `${month ? `${month.slice(0, 3)} ` : ""}${ym.substr(0, 4)}`;
};
const country = (loc) =>
  !loc ? "—" : loc.length <= 3 ? loc.toUpperCase() : loc.replace("-e", " E").replace(/^./, (c) => c.toUpperCase());
const shortDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
const whole = (minor) => (minor ? Math.floor(minor / 100) * 100 : null);

const SOURCE_TONE = { trade: "trade", chrono24: "ch24" };

const Tile = ({ label, children, sub }) => (
  <div className="qws-tile">
    <span className="qws-tile__label">{label}</span>
    <span className="qws-tile__value">{children}</span>
    {sub ? <span className="qws-tile__sub">{sub}</span> : null}
  </div>
);
const points = (n) => (n ? `${n} price${n === 1 ? "" : "s"}` : "No prices");

// The WhatsApp message behind a price, big: photo, the original text, who sent
// it and when. Opened from the row's photo.
function MessageModal({ open, onClose, image, message }) {
  const when = message?.message_date ? new Date(String(message.message_date).replace(" ", "T")) : null;
  return (
    <Modal open={open} onClose={onClose} size="lg" title="WhatsApp message">
      <div className="qws-msg">
        {image ? (
          <a href={image} target="_blank" rel="noopener noreferrer" className="qws-msg__image" title="Open the full-size image">
            <img src={image} alt="Photo sent with the price" />
          </a>
        ) : null}
        <dl className="qws-msg__meta">
          {message?.sender_name || message?.sender_phone ? (
            <div>
              <dt>From</dt>
              <dd>{[message.sender_name, message.sender_phone].filter(Boolean).join(" · ")}</dd>
            </div>
          ) : null}
          {message?.group_name ? (
            <div>
              <dt>Group</dt>
              <dd>{message.group_name}</dd>
            </div>
          ) : null}
          {when && !Number.isNaN(when.getTime()) ? (
            <div>
              <dt>Sent on</dt>
              <dd>
                {when.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </dd>
            </div>
          ) : null}
        </dl>
        <p className="qws-msg__text">
          {message?.message_text || message?.message || "The message text isn't available for this price."}
        </p>
        {image ? (
          <a href={image} target="_blank" rel="noopener noreferrer" className="qws-msg__link">
            <Icon name="ExternalLink" size="sm" /> Open the image in a new tab
          </a>
        ) : null}
      </div>
    </Modal>
  );
}

// One price per row, every row the same height (photo or not):
//   Sent on · Photo · Source · Supplier (name / store) · Details (contact /
//   condition · box · year) · Country · Price · actions
// Text wraps inside its column rather than being cut. A phone-width slot keeps
// the photo and the price and stacks the rest between them.
function PriceRow({ item, dealFinder, onRemoved }) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(false);
  // A trade price with a listing code came from a WhatsApp message.
  const { isTrade, match, resolved } = useTradeMatch(dealFinder ? null : item);
  const message = dealFinder ? item : match;
  const image = message?.whatsapp_image_url || null;

  const remove = async () => {
    setRemoving(true);
    setError(null);
    try {
      await axios.delete(
        dealFinder ? `/api/unapproved-product-prices/${item.id}/delete` : `/api/product-prices/${item.id}/delete`
      );
      onRemoved();
    } catch (e) {
      setError("Couldn't remove this price — try again.");
    } finally {
      setRemoving(false);
      setConfirming(false);
    }
  };

  const source = dealFinder ? "WhatsApp" : sourceName(item.source);
  const tone = dealFinder ? "trade" : SOURCE_TONE[item.source] || "neutral";
  const supplier = dealFinder
    ? [item.group_name, item.sender_name].filter(Boolean).join(" · ")
    : item.supplier_id
    ? item.supplier_name
    : item.supplier;
  const contact = message?.sender_phone || item.sender_phone || item.supplier_phone || item.supplier_email;
  const details = [
    conditions(item.condition),
    boxLabels[dealFinder ? item.delivery_set : item.box],
    dealFinder ? item.year_month?.substr(0, 4) : yearMonth(item.year_month),
  ].filter(Boolean);
  // When the message came in; a price without one falls back to its price date.
  const sentOn = message?.message_date || item.message_date || item.price_date;
  const hasMessage = !!(image || message?.message_text || message?.message);
  const listingUrl = /^https?:\/\//i.test(item.listing_link || "") ? item.listing_link : null;

  return (
    <li className={`qws-price${item.under_priced ? " is-deal" : ""}`}>
      <span className="qws-price__date">{shortDate(sentOn)}</span>
      <span className="qws-price__photo">
        {image ? (
          <button type="button" className="qws-price__thumb" onClick={() => setViewing(true)} aria-label="Open the WhatsApp message">
            <img src={image} alt="" loading="lazy" />
          </button>
        ) : isTrade && !resolved ? (
          <span className="qws-price__thumb is-empty" role="img" aria-label="Loading photo" />
        ) : hasMessage ? (
          <button type="button" className="qws-price__thumb is-empty" onClick={() => setViewing(true)}>
            No photo
          </button>
        ) : (
          // An empty frame keeps every row the same height and the columns aligned.
          <span className="qws-price__thumb is-empty">{isTrade ? "No photo" : ""}</span>
        )}
      </span>
      <span className="qws-price__source-cell">
        <span className={`qws-price__source qws-price__source--${tone}`}>{source}</span>
      </span>
      <span className="qws-price__who" title={[supplier, item.store_name].filter(Boolean).join(" · ")}>
        <span className="qws-price__name">{supplier || "—"}</span>
        {item.store_name ? <span className="qws-price__store">{item.store_name}</span> : null}
      </span>
      <span className="qws-price__details" title={[contact, ...details].filter(Boolean).join(" · ")}>
        <span className="qws-price__inline-date">{shortDate(sentOn)}</span>
        {contact ? <span className="qws-price__contact">{contact}</span> : null}
        <span>{details.join(" · ") || "—"}</span>
      </span>
      <span className="qws-price__country">{country(dealFinder ? item.region : item.location)}</span>
      <span className="qws-price__amount">
        <Money value={whole(item.gbp_price)} currency="gbp" decimals={0} tone="strong" />
        {dealFinder && item.trade_median ? (
          <span className="qws-price__muted">
            Trade median <Money value={item.trade_median} currency="gbp" decimals={0} />
            {item.match_confidence ? ` · ${item.match_confidence}` : ""}
          </span>
        ) : null}
      </span>
      <span className="qws-price__actions">
        {/* Only a real web link (e.g. the Chrono24 listing). The WhatsApp
            source is the photo's pop-up. */}
        {listingUrl ? (
          <IconButton
            variant="ghost"
            size="sm"
            label="Open the listing in a new tab"
            href={listingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="ExternalLink" size="sm" />
          </IconButton>
        ) : null}
        <IconButton variant="ghost" size="sm" label="Remove price" onClick={() => setConfirming(true)}>
          <Icon name="X" size="sm" />
        </IconButton>
      </span>
      {confirming ? (
        <div className="qws-confirm qws-price__confirm" role="group" aria-label="Remove this price">
          <span>Remove this price?</span>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Keep
          </Button>
          <Button variant="danger" size="sm" loading={removing} onClick={remove}>
            Remove
          </Button>
        </div>
      ) : null}
      {error ? <p className="qws-price__error" role="alert">{error}</p> : null}
      {hasMessage ? (
        <MessageModal open={viewing} onClose={() => setViewing(false)} image={image} message={message} />
      ) : null}
    </li>
  );
}

function PriceListCard({ product, user, adding, onAdd }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(PRODUCT_PRICE_LIST_TABS.historical);
  const [filters, setFilters] = useState(TAB_DEFAULTS[PRODUCT_PRICE_LIST_TABS.historical]);
  const { items, total, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useProductPriceList(product?.id, tab, filters, { enabled: !!product?.id });

  const pickTab = (next) => {
    setTab(next);
    setFilters(TAB_DEFAULTS[next]);
  };
  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value || undefined }));
  const filtered = Object.entries(filters).some(([k, v]) => v && v !== TAB_DEFAULTS[tab][k]);
  const invalidate = () =>
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === PRODUCT_PRICE_LIST_KEY && q.queryKey[1] === product?.id,
    });
  const dealFinder = tab === PRODUCT_PRICE_LIST_TABS.dealFinder;

  const filter = (key, label, options) => (
    <Select
      key={key}
      size="md"
      label={label}
      placeholder={label}
      options={options}
      value={filters[key] ?? ""}
      onChange={setFilter(key)}
      clearable
      className="qws-filter"
    />
  );

  return (
    <section className="qws-card" aria-label="Prices">
      <div className="qws-card__head">
        <h4 className="qws-card__title">Prices</h4>
        <Segmented
          size="sm"
          label="Which prices"
          options={TABS.map((t) => ({ ...t, count: t.value === tab && total ? total : undefined }))}
          value={tab}
          onChange={pickTab}
        />
        {!adding && (
          <Button variant="secondary" size="md" className="qws-card__action" leadingIcon={<Icon name="Plus" size="sm" />} onClick={onAdd}>
            Add price
          </Button>
        )}
      </div>

      <div className="qws-filters" role="group" aria-label="Filter prices">
        {filter("year", "Year", toOptions(yearOptions))}
        {filter("condition", "Condition", toOptions(conditionOptions))}
        {filter("box", "Box & papers", toOptions(boxOptions))}
        {user?.is_admin && filter("source", "Source", toOptions(sourceOptions))}
        {user?.is_admin && filter("location", "Country", toOptions(locationOptions))}
        {user?.is_admin && filter("priceSortBy", "Sort", SORT_OPTIONS)}
        {filtered && (
          <Chip onClick={() => setFilters(TAB_DEFAULTS[tab])} icon={<Icon name="X" size="sm" />}>
            Clear
          </Chip>
        )}
        <div className="qws-filters__links">
          <MarketplaceActions item={product} fieldClassName="" />
        </div>
      </div>

      {isLoading && !items.length ? (
        <p className="qws-hint">Loading prices…</p>
      ) : isError && !items.length ? (
        <p className="qws-hint" role="alert">
          Couldn't load the prices.{" "}
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </p>
      ) : !items.length ? (
        <p className="qws-hint">
          {filtered ? "No prices match these filters." : "No prices for this watch yet. They appear as deals are recorded, or add one."}
        </p>
      ) : (
        <>
          <div className="qws-prices__cols" aria-hidden="true">
            <span>Sent on</span>
            <span>Photo</span>
            <span>Source</span>
            <span>{dealFinder ? "Group · sender" : "Supplier"}</span>
            <span>Details</span>
            <span>Country</span>
            <span className="qws-prices__right">Price</span>
            <span />
          </div>
          <ul className="qws-prices">
            {items.map((item) => (
              <PriceRow
                key={`${tab}-${item.id}`}
                item={item}
                dealFinder={dealFinder}
                onRemoved={invalidate}
              />
            ))}
          </ul>
          {hasNextPage && (
            <div className="qws-more-row">
              <Button variant="ghost" size="md" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                Show more
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The workspace's Pricing tab — the modal's Pricing tab (add price, chart,
 * market / trade figures, history · available · deal finder), laid out like
 * every other tab: figure tiles, then cards. Same data, same calls.
 */
export default function WorkspacePricing({ product }) {
  const { user } = useContext(UserContext);
  const { analytics } = usePriceAnalytics(product);
  const { prices, isLoadingPrices, errorPrices } = usePricesByDate(product);
  const refresh = useRefreshPricingData(product?.id);
  const [range, setRange] = useState(DEFAULT_PRICE_CHART_RANGE);
  const [adding, setAdding] = useState(false);

  const seesMarket = user?.is_admin || user?.can_see_market_prices;
  const seesTrade = user?.is_admin || user?.can_see_trade_prices;
  if (!seesMarket && !seesTrade) return null;

  const market = analytics?.market || {};
  const trade = analytics?.trade || {};
  const figureRows = [
    seesMarket && ["Market", market],
    seesTrade && ["Trade", trade],
  ].filter(Boolean);

  return (
    <>
      <div className="qws-tiles qws-tiles--4">
        {seesTrade && (
          <Tile label="Trade · 1M" sub={points(trade.month?.points)}>
            <Money value={trade.month?.gbp || null} currency="gbp" decimals={0} />
          </Tile>
        )}
        {seesMarket && (
          <Tile label="Market · 3M" sub={points(market.quarter?.points)}>
            <Money value={market.quarter?.gbp || null} currency="gbp" decimals={0} />
          </Tile>
        )}
        {seesMarket && (
          <Tile label="Market · 12M" sub={points(market.year?.points)}>
            <Money value={market.year?.gbp || null} currency="gbp" decimals={0} />
          </Tile>
        )}
        {seesMarket && (
          <Tile label="Evolution">
            <PriceEvolution analytics={market} />
          </Tile>
        )}
      </div>

      <section className="qws-card" aria-label="Price history">
        <div className="qws-card__head">
          <h4 className="qws-card__title">Price history</h4>
          <span className="qws-legend" aria-hidden="true">
            <span><i className="qws-legend__dot qws-legend__dot--market" />Market</span>
            <span><i className="qws-legend__dot qws-legend__dot--trade" />Trade</span>
          </span>
          <Segmented
            size="sm"
            label="Chart range"
            options={[...PRICE_CHART_RANGE_OPTIONS].reverse().map((o) => ({ value: o.id, label: o.label }))}
            value={range}
            onChange={setRange}
            className="qws-card__action"
          />
          <IconButton variant="ghost" size="sm" label="Refresh price data" onClick={() => refresh(product?.id)}>
            <Icon name="RefreshCw" size="sm" />
          </IconButton>
        </div>
        <div className="qws-chart">
          <PriceChart prices={prices} dateRange={range} isLoading={isLoadingPrices} error={errorPrices} />
        </div>
        <div className="qws-figures" role="table" aria-label="Market and trade figures">
          <div role="row" className="qws-figures__row qws-figures__row--head">
            <span role="columnheader" />
            {["Actual", "3M", "12M", "Retail £", "Retail €", "Evolution"].map((h) => (
              <span role="columnheader" key={h}>{h}</span>
            ))}
          </div>
          {figureRows.map(([name, a]) => (
            <div role="row" className="qws-figures__row" key={name}>
              <span role="rowheader">{name}</span>
              <span role="cell"><Money value={a.month?.gbp || null} currency="gbp" decimals={0} /></span>
              <span role="cell"><Money value={a.quarter?.gbp || null} currency="gbp" decimals={0} /></span>
              <span role="cell"><Money value={a.year?.gbp || null} currency="gbp" decimals={0} /></span>
              <span role="cell"><Money value={a.retail?.gbp || null} currency="gbp" decimals={0} /></span>
              <span role="cell"><Money value={a.retail?.eur || null} currency="eur" decimals={0} /></span>
              <span role="cell"><PriceEvolution analytics={a} /></span>
            </div>
          ))}
        </div>
      </section>

      {adding && (
        <section className="qws-card" aria-label="Add a price">
          <div className="qws-card__head">
            <h4 className="qws-card__title">Add a price</h4>
          </div>
          <AddProductPriceForm
            layout="grid"
            productId={product.id}
            item={product}
            onCancel={() => setAdding(false)}
            onDone={() => {
              setAdding(false);
              refresh(product?.id);
            }}
            setCheckUnderPrice={(c) => c?.underpriced && refresh(product?.id)}
          />
        </section>
      )}

      {user?.is_admin && <PriceListCard product={product} user={user} adding={adding} onAdd={() => setAdding(true)} />}
    </>
  );
}
